import React from 'react';
import Tooltip, { RichExplanation } from './Tooltip.jsx';
import { lookupKPIGlossary } from './kpiGlossary.js';
import { useGuides } from './GuidesContext.jsx';
import ProvenanceChip from './ProvenanceChip.jsx';

/* KPICard — single-number display card at the top of tabs.
   Label is wrapped in a rich tooltip when:
   - guides.tooltips is on, AND
   - the KPI glossary has an entry for the label text (string only)
   Otherwise the label renders plain. Labels that are React elements
   (e.g. "<>DSO <EduTip/></>") carry their own affordance via EduTip.

   Proposal 8 Tier 2 (2026-04-25) — `provenance` prop renders a chip
   in the top-right of the card answering "where does this number
   come from?". D1 accepts explicit source-state classes and still maps
   legacy values like 'ledger', 'schedule', 'forecast', and 'computed'
   to safe labels. Optional `provenanceTip` overrides the chip tooltip. */
export default function KPICard({ label, value, sub, tone, accent, spark, provenance, provenanceTip }) {
  const { tooltips } = useGuides();
  const base = 'bg-surface border border-border border-t-[3px] rounded-[10px] px-4 py-[0.85rem] transition-colors duration-200 shadow-sm hover:border-border-strong';
  const toneBorder = tone === 'good' ? 'border-t-accent-green'
                   : tone === 'warn' ? 'border-t-accent-gold'
                   : tone === 'bad'  ? 'border-t-accent-red'
                   : 'border-t-border-strong';
  const glossary = tooltips ? lookupKPIGlossary(label) : null;
  const labelNode = glossary
    ? (
      <Tooltip
        placement="top"
        delay={120}
        maxWidth={340}
        content={
          <RichExplanation
            title={typeof label === 'string' ? label : undefined}
            formula={glossary.formula}
            hint={glossary.hint}
            explanation={glossary.explanation}
            direction={glossary.direction}
            betterWhen={glossary.betterWhen}
          />
        }
      >
        <span className="cursor-help">{label}</span>
      </Tooltip>
    )
    : label;
  return (
    <div className={`${base} ${toneBorder}`} style={accent ? { borderTopColor: accent } : undefined}>
      <div className="flex items-start justify-between gap-2 mb-[0.35rem]">
        <div className="text-[0.70rem] uppercase tracking-[0.08em] text-text-dim flex-1 min-w-0">{labelNode}</div>
        {provenance && (
          <ProvenanceChip kind={provenance} tooltip={provenanceTip} compact />
        )}
      </div>
      <div className="text-[1.3rem] font-semibold tabular-nums tracking-[-0.01em] text-text leading-[1.2]">{value}</div>
      {spark && <div className="mt-1 mb-[0.1rem] block">{spark}</div>}
      {sub && <div className="text-[0.73rem] text-text-muted mt-[0.3rem] leading-[1.4]">{sub}</div>}
    </div>
  );
}
